import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProfileProvider } from "@/context/ProfileContext";
import { PlanProvider } from "@/context/PlanContext";
import { Toaster } from "@/components/ui/sonner";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import AppHeader from "@/components/AppHeader";
import AppNav from "@/components/AppNav";
import InstallPrompt from "@/components/InstallPrompt";
import PageviewTracker from "@/components/PageviewTracker";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ReportView from "@/pages/ReportView";
import Journal from "@/pages/Journal";
import Chat from "@/pages/Chat";
import SharedReport from "@/pages/SharedReport";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="p-16 text-center text-sm" style={{ color: "#5C6B66" }}>
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ConditionalNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user) return null;
  if (["/login", "/register", "/"].includes(pathname)) return null;
  if (pathname.startsWith("/shared/")) return null;
  return <AppNav />;
}

function Shell() {
  const { pathname } = useLocation();
  const isShared = pathname.startsWith("/shared/");
  return (
    <div className="App">
      <PageviewTracker />
      {!isShared && <DisclaimerBanner />}
      {!isShared && <AppHeader />}
      <ConditionalNav />
      <main className="relative">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/shared/:token" element={<SharedReport />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route
            path="/payment/success"
            element={
              <Protected>
                <PaymentSuccess />
              </Protected>
            }
          />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route
            path="/dashboard"
            element={
              <Protected>
                <Dashboard />
              </Protected>
            }
          />
          <Route
            path="/reports/:id"
            element={
              <Protected>
                <ReportView />
              </Protected>
            }
          />
          <Route
            path="/journal"
            element={
              <Protected>
                <Journal />
              </Protected>
            }
          />
          <Route
            path="/chat"
            element={
              <Protected>
                <Chat />
              </Protected>
            }
          />
          <Route
            path="/chat/:sessionId"
            element={
              <Protected>
                <Chat />
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Toaster position="top-right" richColors />
      {!isShared && <InstallPrompt />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <PlanProvider>
            <Shell />
          </PlanProvider>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
